class TabComponent {
  constructor(containerSelector, options = {}) {
    this.container = document.querySelector(containerSelector);
    if (!this.container) {
      console.warn(`Tab container not found: ${containerSelector}`);
      return;
    }

    this.options = {
      mobileBreakpoint: options.mobileBreakpoint || 770,
      activeClass: options.activeClass || "is-active",
      openClass: options.openClass || "is-open",
      dropdownActiveClass: options.dropdownActiveClass || "dropdown-active",
      tabGroupAttribute: options.tabGroupAttribute || "data-tab-id",
      tabPanelAttribute: options.tabPanelAttribute || "data-tab-content",
      ...options,
    };

    this.navContainer = this.container.querySelector(".js-tab-nav");
    this.navLinks = this.container.querySelectorAll(".js-tab-nav a");

    // Get the tab group ID from the container
    this.tabGroupId = this.container.id;

    // Find content container - can be anywhere in the DOM with matching data-tab-id
    this.contentContainer = document.querySelector(
      `[${this.options.tabGroupAttribute}="${this.tabGroupId}"]`,
    );

    // Find tab items - look for items with data-tab-content attribute
    this.contentItems = this.contentContainer
      ? this.contentContainer.querySelectorAll(
          `[${this.options.tabPanelAttribute}]`,
        )
      : [];

    if (!this.navContainer) {
      console.warn("Required tab navigation not found");
      return;
    }

    if (!this.contentContainer) {
      console.warn(
        `Content container not found for tab group: ${this.tabGroupId}`,
      );
      return;
    }

    this.isMobile = window.innerWidth <= this.options.mobileBreakpoint;
    this.isInitialized = false;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.bindEvents();
    if (this.isMobile) {
      this.setupMobile();
    }

    // Set initial active state if none exists
    const activeLink = this.container.querySelector(
      ".js-tab-nav a." + this.options.activeClass,
    );
    if (!activeLink && this.navLinks.length > 0) {
      const firstCategory = this.navLinks[0].dataset.tabCategory;
      this.setActiveTab(this.navLinks[0]);
      this.filterContent(firstCategory);

      // Update the active tab text for the first tab
      const $tabActive = $(this.container.querySelector(".js-tab-active"));
      $tabActive.text(this.navLinks[0].textContent.trim());
    } else if (activeLink) {
      const activeCategory = activeLink.dataset.tabCategory;
      this.filterContent(activeCategory);

      // Update the active tab text for the existing active tab
      const $tabActive = $(this.container.querySelector(".js-tab-active"));
      $tabActive.text(activeLink.textContent.trim());
    }

    this.isInitialized = true;
  }

  bindEvents() {
    // Tab click events
    this.navLinks.forEach((link) => {
      link.addEventListener("click", (e) => this.handleTabClick(e));
    });

    // Mobile tab active element click event
    const tabActiveElement = this.container.querySelector(".js-tab-active");
    if (tabActiveElement) {
      tabActiveElement.addEventListener("click", (e) =>
        this.handleTabActiveClick(e),
      );
    }

    // Outside click for mobile dropdown
    document.addEventListener("click", (e) => this.handleOutsideClick(e));

    // Window resize
    window.addEventListener("resize", () => this.handleResize());
  }

  handleTabClick(e) {
    e.preventDefault();
    const link = e.target;
    // Support both data-tab-category and data-category attributes
    const category = link.dataset.tabCategory;

    if (this.isMobile) {
      if (link.classList.contains(this.options.activeClass)) {
        const $tabNav = $(this.navContainer);
        $tabNav.slideToggle(300);

        // Toggle the is-open class on the tab active element for styling
        const $tabActive = $(this.container.querySelector(".js-tab-active"));
        $tabActive.toggleClass("is-open");
        return;
      }

      // Update dropdown active state
      this.container.querySelectorAll(".js-tab-nav li").forEach((li) => {
        li.classList.remove(this.options.dropdownActiveClass);
      });
      link.parentElement.classList.add(this.options.dropdownActiveClass);

      // Update the active tab text (mobile dropdown)
      const $tabActive = $(this.container.querySelector(".js-tab-active"));
      $tabActive.text(link.textContent.trim());

      // Close dropdown after selection
      const $tabNav = $(this.navContainer);
      $tabNav.slideUp(300);

      // Remove is-open class from tab active element
      $tabActive.removeClass("is-open");
    }

    this.setActiveTab(link);
    this.filterContent(category);

    // Always update the visible label of current tab (desktop + mobile)
    const tabActiveEl = this.container.querySelector(".js-tab-active");
    if (tabActiveEl) {
      tabActiveEl.textContent = link.textContent.trim();
    }
  }

  handleTabActiveClick(e) {
    if (this.isMobile) {
      e.preventDefault();
      const $tabNav = $(this.navContainer);
      $tabNav.slideToggle(300);

      // Toggle the is-open class on the tab active element for styling
      const $tabActive = $(this.container.querySelector(".js-tab-active"));
      $tabActive.toggleClass("is-open");
    }
  }

  handleOutsideClick(e) {
    if (
      this.isMobile &&
      !e.target.closest(".js-tab-nav") &&
      !e.target.closest(".js-tab-active")
    ) {
      const $tabNav = $(this.navContainer);
      $tabNav.slideUp(300);

      // Remove is-open class from tab active element
      const $tabActive = $(this.container.querySelector(".js-tab-active"));
      $tabActive.removeClass("is-open");
    }
  }

  handleResize() {
    const newIsMobile = window.innerWidth <= this.options.mobileBreakpoint;
    if (newIsMobile !== this.isMobile) {
      this.isMobile = newIsMobile;

      // Close dropdown if switching to desktop
      if (!this.isMobile) {
        const $tabNav = $(this.navContainer);
        $tabNav.slideUp(300);

        // Remove is-open class from tab active element
        const $tabActive = $(this.container.querySelector(".js-tab-active"));
        $tabActive.removeClass("is-open");
      }

      if (this.isMobile) {
        this.setupMobile();
      } else {
        this.container.querySelectorAll(".js-tab-nav li").forEach((li) => {
          li.classList.remove(this.options.dropdownActiveClass);
        });
      }
    }
  }

  setActiveTab(activeLink) {
    // Remove active state from all links
    this.navLinks.forEach((link) => {
      link.classList.remove(this.options.activeClass);
      link.setAttribute("aria-pressed", "false");
    });

    // Set active state
    activeLink.classList.add(this.options.activeClass);
    activeLink.setAttribute("aria-pressed", "true");
  }

  filterContent(category) {
    if (category === "all") {
      this.contentItems.forEach((item) => {
        item.style.display = "block";
      });
    } else {
      this.contentItems.forEach((item) => {
        const panelCategory = item.getAttribute(this.options.tabPanelAttribute);
        if (panelCategory === category) {
          item.style.display = "block";
        } else {
          item.style.display = "none";
        }
      });
    }

    // Fix: Force Slick to recalculate after tab becomes visible
    setTimeout(() => {
      const activePanel = Array.from(this.contentItems).find(
        (item) => item.style.display === "block",
      );
      if (activePanel) {
        const sliders = activePanel.querySelectorAll(".jsTabSlider");
        sliders.forEach((slider) => {
          if ($(slider).hasClass("slick-initialized")) {
            $(slider).slick("setPosition");
          }
        });
      }
    }, 50);

    // Trigger custom event for external listeners
    this.container.dispatchEvent(
      new CustomEvent("tabChanged", {
        detail: {
          category,
          activeTab: this.container.querySelector(
            ".js-tab-nav a." + this.options.activeClass,
          ),
          contentContainer: this.contentContainer,
          tabGroupId: this.tabGroupId,
        },
      }),
    );
  }

  setupMobile() {
    this.container.querySelectorAll(".js-tab-nav li").forEach((li) => {
      li.classList.remove(this.options.dropdownActiveClass);
    });

    const activeLink = this.container.querySelector(
      ".js-tab-nav a." + this.options.activeClass,
    );
    if (activeLink) {
      activeLink.parentElement.classList.add(this.options.dropdownActiveClass);
    }
  }

  // Public methods for external control
  setActiveCategory(category) {
    const targetLink = this.container.querySelector(
      `[data-tab-category="${category}"]`,
    );
    if (targetLink) {
      this.setActiveTab(targetLink);
      this.filterContent(category);
    }
  }

  destroy() {
    this.navLinks.forEach((link) => {
      link.removeEventListener("click", this.handleTabClick);
    });
    document.removeEventListener("click", this.handleOutsideClick);
    window.removeEventListener("resize", this.handleResize);
    this.isInitialized = false;
  }
}

// Export for use as module
if (typeof module !== "undefined" && module.exports) {
  module.exports = TabComponent;
}

// Global access
window.TabComponent = TabComponent;

//Cursor
function cursorClick() {
  $("a").each(function () {
    let href = $(this).attr("href");
    if (!href || href === "#" || href === "#0" || href === "#!") {
      $(this).addClass("is-unclickable");
    }
  });
}

//footer accordion
function footerAccordion() {
  if ($(window).width() <= 991 && $(".footer__links").length > 0) {
    $(".footer__links h3").on("click", function () {
      const $clickedHeader = $(this);
      const $currentContent = $clickedHeader.next("ul");
      const $currentSection = $clickedHeader.closest(".footer__links");

      // Slide up all other contents
      $(".footer__links ul").not($currentContent).slideUp(300);

      // Remove is-open from all others
      $(".footer__links").not($currentSection).removeClass("is-open");

      // Toggle current
      $currentContent.slideToggle(300);
      $currentSection.toggleClass("is-open");
    });
  }
}

// Initialize reusable tab components
function initTabComponents() {
  // Find all tab containers and initialize them
  const tabContainers = document.querySelectorAll(
    ".umh.tab, [data-tab-component]",
  );
  tabContainers.forEach((container, index) => {
    // Create a unique identifier if none exists
    if (!container.id) {
      container.id = `tab-component-${index}`;
    }

    // Initialize the tab component
    new TabComponent(`#${container.id}`);
  });
}

// tab slider
const tabSlider = () => {
  if ($(".jsTabSlider").length > 0) {
    $(".jsTabSlider").slick({
      dots: false,
      infinite: false,
      speed: 300,
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: true,
      prevArrow:
        '<button type="button" class="slick-prev" aria-label="Previous testimonial"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>',
      nextArrow:
        '<button type="button" class="slick-next" aria-label="Next testimonial"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>',
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 991,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
    });

    // Force display of slider after initialization, so that slider won't break in tab change
    $(".jsTabSlider").css("display", "block");
  }
};

// Testimonial slider using Slick
const testimonialSlider = () => {
  if (!$(".testimonial-slider__grid").length) return;

  $(".testimonial-slider__grid").slick({
    infinite: true,
    fade: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnHover: true,
    arrows: true,
    prevArrow:
      '<button type="button" class="slick-prev" aria-label="Previous testimonial"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>',
    nextArrow:
      '<button type="button" class="slick-next" aria-label="Next testimonial"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>',
  });
};

// Universal video dialog functionality
function videoDialog() {
  const $dialog = $("#video-dialog");
  if ($dialog.length === 0) return;

  const $dialogWrap = $(".video-dialog__wrap");
  let $lastTrigger = null; // Store the element that triggered the dialog

  // Universal video trigger - works with any element with data-video-id
  $(document).on("click", "[data-video-id]", function (e) {
    e.preventDefault();
    $lastTrigger = $(this); // Store reference for focus return
    const videoId = $(this).attr("data-video-id");
    const videoUrl = $(this).attr("data-video-url"); // Alternative: full URL
    openVideoDialog(videoId, videoUrl);
  });

  // Close button click
  $(document).on("click", ".video-dialog__close", function () {
    closeVideoDialog();
  });

  // Dialog close event (handles ESC key and backdrop click automatically)
  $dialog.on("close", function () {
    // Clear iframe when dialog closes
    $dialogWrap.find("iframe").remove();

    // Return focus to the element that triggered the dialog
    if ($lastTrigger) {
      $lastTrigger.focus();
      $lastTrigger = null;
    }
  });

  function openVideoDialog(videoId, videoUrl) {
    if (!videoId && !videoUrl) return;

    let iframeSrc;

    if (videoUrl) {
      // Use custom video URL
      iframeSrc = videoUrl;
    } else if (videoId) {
      // Build YouTube URL from video ID
      iframeSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
    }

    // Create iframe
    const $iframe = $(`
      <iframe 
        src="${iframeSrc}" 
        title="Video Player" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen>
      </iframe>`);

    // Clear existing content and add iframe
    $dialogWrap.find("iframe").remove();
    $dialogWrap.append($iframe);

    // Show dialog using native showModal method
    $dialog[0].showModal();

    // Focus management
    $(".video-dialog__close").focus();
  }

  function closeVideoDialog() {
    // Close dialog using native close method
    $dialog[0].close();
  }

  // Public API for programmatic use
  window.VideoDialog = {
    open: openVideoDialog,
    close: closeVideoDialog,
  };
}

function searchBox() {
  const $searchBox = $(".js-search-box");
  const $searchInput = $searchBox.find("input");
  const $searchClearBtn = $searchBox.find(".l-clear-btn");

  $searchClearBtn.on("click", function () {
    $searchInput.val("");
    $searchInput.focus();
  });
}

function simpleSlider() {
  if (!$(".js-simple-slider").length) return;

  $(".js-simple-slider").slick({
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          autoplay: true,
          slidesToShow: 1,
        },
      },
    ],
  });
}

function syncSlider() {
  if ($(".sync-slider").length > 0) {
    $(".sync-slider__highlight").slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      fade: true,
      asNavFor: ".sync-slider__nav",
    });
    $(".sync-slider__nav").slick({
      slidesToShow: 6,
      slidesToScroll: 1,
      asNavFor: ".sync-slider__highlight",
      dots: false,
      arrows: false,
      centerMode: true,
      focusOnSelect: true,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 4,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 3,
          },
        },
      ],
    });
  }
}

// advanced filter
function filterToggle() {
  const $filterBtn = $(".jsFilterBtn");
  const $filterAdvanced = $(".jsFilterAdvanced");

  if ($filterBtn.length && $filterAdvanced.length) {
    $filterBtn.on("click", function (e) {
      e.preventDefault();

      const isVisible = $filterAdvanced.hasClass("is-active");

      if (isVisible) {
        // Hide the filter with smooth animation
        $filterAdvanced.slideUp(300, function () {
          $filterAdvanced.removeClass("is-active");
          $filterBtn.text("Advanced Filter +");
        });
      } else {
        // Show the filter with smooth animation
        $filterAdvanced.slideDown(300, function () {
          $filterAdvanced.addClass("is-active");
          $filterBtn.text("Close x");
        });
      }
    });

    // Close filter when clicking outside
    $(document).on("click", function (e) {
      if (
        $filterAdvanced.hasClass("is-active") &&
        !$filterAdvanced.is(e.target) &&
        !$filterAdvanced.has(e.target).length &&
        !$filterBtn.is(e.target) &&
        !$filterBtn.has(e.target).length
      ) {
        $filterAdvanced.slideUp(300, function () {
          $filterAdvanced.removeClass("is-active");
          $filterBtn.text("Advanced Filter +");
        });
      }
    });
  }
}

// Price range slider
function priceRangeSlider() {
  if ($(".jsPriceRange").length > 0) {
    try {
      $(".jsPriceRange").slider({
        step: 500,
        range: true,
        min: 0,
        max: 20000,
        values: [0, 20000],
        slide: function (event, ui) {
          $(".jsMinPrice").text("$" + ui.values[0].toLocaleString());
          $(".jsMaxPrice").text("$" + ui.values[1].toLocaleString());
        },
      });

      // Set initial display values
      $(".jsMinPrice").text(
        "$" + $(".jsPriceRange").slider("values", 0).toLocaleString(),
      );
      $(".jsMaxPrice").text(
        "$" + $(".jsPriceRange").slider("values", 1).toLocaleString(),
      );
    } catch (error) {
      console.error("Error initializing slider:", error);
    }
  }
}

// Call the function on window load
function lSelectHandler() {
  if ($(".l-select").length > 0) {
    $(".l-select select").each(function () {
      var $this = $(this),
        selectOptions = $(this).children("option").length,
        multiple = $(this).prop("multiple");

      $this.addClass("l-select__hide");
      $this.wrap('<div class="l-select__wrap"></div>');
      $this.after(
        '<div class="l-select__selected"><span></span><i><svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.20711 8.70711C7.81658 9.09763 7.18342 9.09763 6.79289 8.70711L0.428928 2.34315C0.0384032 1.95263 0.0384026 1.31946 0.428927 0.928938C0.819451 0.538413 1.45262 0.538413 1.84314 0.928937L7.5 6.58579L13.1568 0.928929C13.5474 0.538404 14.1805 0.538403 14.5711 0.928928C14.9616 1.31945 14.9616 1.95262 14.5711 2.34314L8.20711 8.70711ZM8.5 6L8.5 8L6.5 8L6.5 6L8.5 6Z" fill="#6B8FA3"></path></svg></i></div>',
      );
      var $customSelect = $this.next("div.l-select__selected").children("span");

      var $optionlist = $("<ul />", {
        class: "l-select__options",
      }).insertAfter($customSelect.parent());

      if ($this.children("option").filter(":selected")) {
        $customSelect.text($this.children("option").filter(":selected").text());
      } else {
        $customSelect.text($this.children("option").eq(0).text());
      }

      for (var i = 0; i < selectOptions; i++) {
        $("<li />", {
          text: $this.children("option").eq(i).text(),
          rel: $this.children("option").eq(i).val(),
          "data-category": $this.children("option").eq(i)[0].attributes[
            "data-category"
          ].nodeValue,
        }).appendTo($optionlist);
      }

      var $optionlistItems = $optionlist.children("li");

      // Add "is-hidden" class to the first item by default
      $optionlistItems.eq(0).addClass("is-hidden");

      $optionlistItems.click(function (e) {
        e.stopPropagation();

        // Remove "is-hidden" class from all items
        $optionlistItems.removeClass("is-hidden");

        // Add "is-hidden" class to the selected item
        $(this).addClass("is-hidden");

        $customSelect.text($(this).text()).parent().removeClass("is-open");
        $this.val($(this).attr("rel"));
        $optionlist.hide();
        $this.trigger("change");
        $(".l-select").removeClass("is-open");
      });

      $customSelect.parent().click(function (e) {
        e.stopPropagation();
        $("div.l-select__selected.is-open")
          .not(this)
          .each(function () {
            $(this).removeClass("is-open").next("ul.l-select__options").hide();
          });
        $(this)
          .toggleClass("is-open")
          .parents(".l-select")
          .toggleClass("is-open");

        if ($(".l-select--sort").length != 0) {
          if ($(window).width() < 767) {
            $(this)
              .toggleClass("is-open")
              .next("ul.l-select__options")
              .fadeToggle();
          } else {
            $(this)
              .toggleClass("is-open")
              .next("ul.l-select__options")
              .slideToggle();
          }
        } else {
          $(this)
            .toggleClass("is-open")
            .next("ul.l-select__options")
            .slideToggle();
        }
      });

      $(document).click(function () {
        $customSelect.parent().removeClass("is-open");
        $optionlist.hide();
        $(".l-select").removeClass("is-open");
      });
    });
  }
// Static tab navigation (simple hash links)
function staticTabNavigation() {
  const $staticNavs = $(".js-static-tab-nav");
  if (!$staticNavs.length) return;

  // Helper to set active state within a nav group
  const setActive = ($link) => {
    const $nav = $link.closest(".js-static-tab-nav");
    $nav.find("a").removeClass("is-active");
    $link.addClass("is-active");
  };

  // Initial state based on current URL hash (if any)
  if (window.location.hash) {
    const $initialLink = $staticNavs.find(
      `a[href="${window.location.hash}"]`,
    );
    if ($initialLink.length) {
      setActive($initialLink);
    }
  }

  // Click handler
  $staticNavs.on("click", "a", function () {
    const $link = $(this);

    // Update active state within this nav group
    setActive($link);
    // Allow default browser behaviour to handle hash navigation/scroll.
  });
}
}

$(document).ready(function () {
  searchBox();
  staticTabNavigation();
});

$(window).on("load", function () {
  cursorClick();
  footerAccordion();
  initTabComponents();
  tabSlider();
  testimonialSlider();
  videoDialog();
  simpleSlider();
  syncSlider();
  filterToggle();
  priceRangeSlider();
  lSelectHandler();
});

$(window).on("orientationchange", function () {
  tabSlider();
  syncSlider();
});
